import { useMemo, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';

/**
 * Splits a react-router v5 location descriptor into the `to` and `options`
 * arguments that react-router v8's `navigate` expects.
 *
 * v5 carried the navigation state inside the location object
 * (`push({ pathname, state })`); v8 takes it as a separate option.
 */
const splitTo = (to, state) => {
    if (to && typeof to === 'object' && to.state !== undefined) {
        const { state: embeddedState, ...rest } = to;
        return [rest, embeddedState];
    }
    return [to, state];
};

/**
 * Exposes the react-router v5 `history` API on top of react-router v8 so that
 * existing call sites keep working after the upgrade. New code should use
 * `useNavigate`/`useLocation` directly.
 *
 * Like v5's history singleton, the returned object keeps the same identity for
 * the component's lifetime, so effects listing it as a dependency don't re-run
 * on every navigation. Its methods call the latest `navigate`, and `location`
 * and `action` read the current values, as v5's `history.location` did.
 */
export default function useHistory() {
    const navigate = useNavigate();
    const location = useLocation();
    const action = useNavigationType();

    // Written during render so `history.location` is already current in the
    // render that follows a navigation.
    const latest = useRef();
    latest.current = { navigate, location, action };

    return useMemo(
        () => ({
            push(to, state) {
                const [target, navState] = splitTo(to, state);
                latest.current.navigate(target, { state: navState });
            },
            replace(to, state) {
                const [target, navState] = splitTo(to, state);
                latest.current.navigate(target, { replace: true, state: navState });
            },
            go(delta) {
                latest.current.navigate(delta);
            },
            goBack() {
                latest.current.navigate(-1);
            },
            goForward() {
                latest.current.navigate(1);
            },
            get location() {
                return latest.current.location;
            },
            get action() {
                return latest.current.action;
            },
            get length() {
                return typeof window === 'undefined' ? 0 : window.history.length;
            },
        }),
        []
    );
}
